import { useState, useEffect } from 'react'
import { Alert, Box, Button, Typography } from '@mui/material';
import { AttendeeDto, AttendeeListDto } from '../../../Component/Model/Dto/WSP/Attendees/MerSETAAttendees';
import { MerSETAInternalAttendeeService } from '../../../Component/Api/Attendee/MerSETAInternalAttendeeService';
import AppSelectListWithInfoAndSearch from '../../../Component/Common/Components/FormValidationInputs/AppSelectListWithInfoAndSearch';
import { useForm } from 'react-hook-form';
import '../../../Component/Common/Components/DataTable/DataTable.scss';
import { toast } from 'react-toastify';
import { ConfirmationDialog } from '../../../Component/Common/Components/ConfirmationDialog';
import { defaultObject } from '../../../Helpers/typescriptHelper';
import { AttendeeLookup } from '../../../Component/Model/Dto/WSP/Attendees/AttendeeLookup';
import { useRecoilValue } from 'recoil';
import { InterventionAtom } from '../PeopleAndTraining/Training/Intervention.Atom';
import { Link } from 'react-router-dom';
import { OrganisationAtom } from '../../../State/Organisation.Atom';
import { DataTable } from '../../../Component/Common/Components/DataTable/DataTable';
import { EntriesPerPage } from '../../../Component/Model/Table/Table';
import { AttendeeTableMapper } from './AttendeeTableMapper';
import { SmallLoading } from '../../../Component/Common/Components/Loading/IntermediateLoading';
import { IntermediateGlobalLoading } from '../../../Component/Common/Components/Loading/IntermediateGlobalLoading';
import { isNullOrEmpty } from '../../../Component/Model/helpers';

interface SelectedAttendee {
    name: string;
    platformPeopleId: string;
}

export const MersetaInternalAttendees = () => {
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });

    const [data, setData] = useState<AttendeeDto[]>([]);
    const [selectedAttendee, setSelectedAttendee] = useState<string[]>([]);
    const [attendees, setAttendees] = useState<AttendeeLookup[]>([]);
    const [showDeleteConfirmDialog, setShowConfirmDeleteDialog] = useState<boolean>(false);
    const [selectedAttendeeToDelete, setSelectedAttendeeToDelete] = useState<SelectedAttendee>(defaultObject);
    const { control } = useForm({
        mode: 'all',
    });
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [clearDropdownKey] = useState<number>(0);
    const [searchValue, setSearchValue] = useState<string|null>(null);
    const selectedSDE = useRecoilValue(InterventionAtom);
    const peopleAndTrainingOrganisation = useRecoilValue(OrganisationAtom);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
    const [filteredItemCount, setFilteredItemCount] = useState<number>(0);
    const [tableRefreshKey, setTableRefreshKey] = useState<number>(0);

    const changeSelectedAttendeeToAdd = (value: string[]) => {
        setSelectedAttendee(value);
    }
    useEffect(() => {
        if (selectedSDE.id > -1) {
            fetchData();
        }
    }, [entriesPerPage, pageNumber, searchValue, tableRefreshKey, selectedSDE]);

    const handleDeleteAttendee = async () => {
        await MerSETAInternalAttendeeService.deleteAttendee(selectedAttendeeToDelete?.platformPeopleId, selectedSDE.id);
        toast.success(`${selectedAttendeeToDelete.name} has been deleted`);
        setShowConfirmDeleteDialog(false);
        fetchData();
    }


    const showDeleteAttendeeConfirmation = async (id: string, name: string) => {
        setSelectedAttendeeToDelete({ platformPeopleId: id, name: name });
        setShowConfirmDeleteDialog(true);
    }

    const addAttendee = async () => {
        let attendeeList: AttendeeListDto = { interventionId: selectedSDE.id, platformPeopleIdList: selectedAttendee };
        if (selectedAttendee.length === 0) {
            toast.error('No attendee has been selected to be added');
            return;
        }
        try {
            await MerSETAInternalAttendeeService.addAttendee(attendeeList).then((newAttendeeAdded) => {
                toast.success(attendeeList.platformPeopleIdList.length + (attendeeList.platformPeopleIdList.length > 1 ? ' Attendees' : ' Attendee') + ' have been added.');
                setSelectedAttendee([]);
                fetchData();
            });
        } catch (error) {
            console.log(error);
        }
    }
    const fetchData = async () => {
        try {
            setIsLoading(true);
            let result = await MerSETAInternalAttendeeService.getAllocatedAttendees(+peopleAndTrainingOrganisation.OrganisationId, selectedSDE.id, pageNumber, entriesPerPage.defaultValue, searchValue ?? '');
            setData(result.data.results);
            setTotalItemsCount(result.data.totalItemsCount);
            setFilteredItemCount(result.data.filteredItemsCount);

            let attendeeResult = await MerSETAInternalAttendeeService.getAttendees(+peopleAndTrainingOrganisation.OrganisationId, selectedSDE.id);
            setAttendees(attendeeResult.data);
        } catch (error) {
            console.log(error);
            setTotalItemsCount(1);
        } finally {
            setIsLoading(false);

        }
    };

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    };

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    };

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
        setPageNumber(1);
    }

    const showNoAttendeesWarning = !isLoading && attendees?.length === 0 && data?.length === 0 && isNullOrEmpty(searchValue);
    const attendeeTableData = AttendeeTableMapper(data, showDeleteAttendeeConfirmation, () => { });

    if (isLoading && searchValue === null) {
        return <IntermediateGlobalLoading />
    }
    else {
        return (
            <>
                <div>
                    <div style={{ paddingBottom: '20px' }}>
                        <Typography variant="body2">
                            You may bulk add attendees to this training intervention.
                        </Typography>
                    </div>

                    {showNoAttendeesWarning ? (
                        <Box pb={3} sx={{ width: '100%' }}>
                            <Alert severity="warning" style={{ paddingTop: 5 }}>
                                There are no available people to add to this training intervention. Please capture people for this organisation.
                            </Alert>
                            <Box pt={1}>
                                <Link to={`/organisation/people`}>
                                    <Button color="primary" variant="contained" >
                                        Manage People
                                    </Button>
                                </Link>
                            </Box>
                        </Box>
                    ) : (
                        <>
                            {attendees?.length > 0 ? (
                                <Box pb={3} sx={{ width: '40%' }}>
                                    <AppSelectListWithInfoAndSearch
                                        key={clearDropdownKey}
                                        control={control}
                                        name="attendee"
                                        label="Select person/people within your organisation"
                                        size="small"
                                        options={attendees}
                                        onChange={(value: string[]) => changeSelectedAttendeeToAdd(value)}
                                        disableEntries={true}
                                        onAdd={() => addAttendee()}
                                    />
                                </Box>
                            ) : (
                                <Box pb={3} sx={{ width: '100%' }}>
                                    <Alert severity="warning" style={{ paddingTop: 5 }}>
                                        All available people have been added to this intervention.
                                    </Alert>
                                </Box>
                            )}


                            <DataTable
                                key={'attendeeTable-' + tableRefreshKey}
                                table={attendeeTableData}
                                entriesPerPage={entriesPerPage}
                                pageNumber={pageNumber}
                                filteredItemsCount={filteredItemCount}
                                totalItemsCount={totalItemsCount}
                                isLoading={isLoading}
                                searchValueChanged={searchValueChanged}
                                canSearch={true}
                                entriesPerPageChanged={entriesPerPageChanged}
                                pageNumberChanged={pageNumberChanged} />

                            <ConfirmationDialog
                                showDialog={showDeleteConfirmDialog}
                                handleAccept={handleDeleteAttendee}
                                handleCancel={() => { setShowConfirmDeleteDialog(false) }}
                                heading={"Delete Attendee"}
                                message={`Are you sure you want to remove ${selectedAttendeeToDelete.name} from this training intervention?`}
                            />
                        </>
                    )}
                </div>
            </>
        )
    };
}